import React, { useState, useMemo, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Tooltip } from 'primereact/tooltip';
import { Delivery, PackageStatus, PACKAGE_STATUS_DESCRIPTIONS, PACKAGE_STATUS_ABREVIATIONS } from '../types';
import { useUserRole } from '../../auth/hooks';
import { usePaquetexpress } from '../hooks';
import { PRINT_URL, UserRoles } from '../../common/utils/util';
import { TableHeader } from '../../common';
import CreateGuideDialog from './create-guide-dialog.component';
import './deliveries-table.css';

interface DeliveriesTableProps {
  deliveries: Delivery[];
  loading: boolean;
  onDeliveryUpdated?: () => void; // Callback para cuando se actualiza una entrega
}

// Definición de la configuración de columnas
interface ColumnConfig {
  field: keyof Delivery | 'actions';
  header: string;
  sortable: boolean;
  style: Object;
  body?: (rowData: Delivery) => React.ReactNode;
  restrictedRoles?: string[];
}

// Formatear la fecha para mostrar en la tabla
const formatDate = (value: string | null) => {
  if (!value) return 'N/A';
  
  return new Date(value).toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    //hour: '2-digit',
    //minute: '2-digit'
  });
};

// Template para las columnas de fecha
const dateTemplate = (rowData: Delivery, field: keyof Delivery) => {
  return formatDate(rowData[field] as string | null);
};

// Template para la columna de estado
const statusTemplate = (rowData: Delivery) => {
  const statusCode = rowData.ESTADO_PAQUETE as PackageStatus;
  const statusDescription = PACKAGE_STATUS_DESCRIPTIONS[statusCode] || 'Estado desconocido';
  const tooltipId = `status-tooltip-${rowData.ENTREGA}`;
  const statusAbbreviation = PACKAGE_STATUS_ABREVIATIONS[statusCode] || 'Estado desconocido';

  return (
    <>
      <span 
        id={tooltipId}
        className={`status-badge status-${rowData.ESTADO_PAQUETE}`}
        data-pr-tooltip={statusDescription}
        data-pr-position="top"
        data-pr-at="center top"
        data-pr-my="center bottom"
        data-pr-class="custom-tooltip"
      >
        {statusAbbreviation}
      </span>
      <Tooltip target={`#${tooltipId}`} />
    </>
  );
};

const phoneTemplate = (rowData: Delivery) => {
  const phone = rowData.CELULAR;
  if (!phone) {
    return <span style={{ color: '#999' }}>N/A</span>;
  }
  
  // Limpiar el número de teléfono (remover espacios, guiones, paréntesis)
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  
  // Verificar si el número tiene el código de país de México (+52)
  const formattedPhone = cleanPhone.startsWith('+52') 
    ? cleanPhone 
    : cleanPhone.startsWith('52') 
      ? `+${cleanPhone}`
      : `+52${cleanPhone}`;
  
  const whatsappUrl = `https://wa.me/${formattedPhone.replace('+', '')}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ 
        color: "#25D366", 
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: "500"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.textDecoration = "underline";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.textDecoration = "none";
      }}
      title="Enviar mensaje por WhatsApp"
    >
      {phone}
    </a>
  );
};

// Configuración de columnas definida fuera del componente
const COLUMN_CONFIGS: ColumnConfig[] = [
  {
    field: 'ENTREGA',
    header: 'Id de Entrega',
    sortable: true,
    style: { width: '12%' },
    restrictedRoles: [UserRoles.VIEWER]
  },
  {
    field: 'REFERENCIA',
    header: 'Referencia',
    sortable: true,
    style: { width: '12%' },
    restrictedRoles: [UserRoles.EDITOR, UserRoles.ADMIN]
  },
  {
    field: 'PEDIDO',
    header: 'Pedido',
    sortable: true,
    style: { width: '12%' },
    restrictedRoles: [UserRoles.EDITOR, UserRoles.ADMIN]
  },
  {
    field: 'IDSAP',
    header: 'Nombre Receptor',
    sortable: true,
    style: { width: '15%' },
    restrictedRoles: [UserRoles.VIEWER]
  },
  {
    field: 'NOMBRE_CLIENTE1',
    header: 'Nombre del Cliente',
    sortable: true,
    style: { width: '15%' },
    restrictedRoles: [UserRoles.EDITOR, UserRoles.ADMIN]
  },
  {
    field: 'CELULAR',
    header: 'Celular',
    sortable: true,
    style: { width: '15%' },
    restrictedRoles: [UserRoles.EDITOR, UserRoles.ADMIN],
    body: phoneTemplate
  },
  {
    field: 'SALIDA_FECHA',
    header: 'Fecha Emisión de Guía',
    sortable: true,
    style: { width: '12%' },
    body: (rowData) => dateTemplate(rowData, 'SALIDA_FECHA'),
    restrictedRoles: [UserRoles.VIEWER]
  },
  {
    field: 'NUMERO_RASTREO',
    header: 'Número de Guía',
    sortable: true,
    style: { width: '15%' }
  },
  {
    field: 'ESTADO',
    header: 'Estado',
    sortable: true,
    style: { width: '10%' }
  },
  {
    field: 'ESTADO_PAQUETE',
    header: 'Estado Paquete',
    sortable: true,
    style: { width: '10%' },
    body: statusTemplate
  },
  {
    field: 'actions',
    header: 'Guias',
    sortable: false,
    style: { width: '10%' },
    restrictedRoles: [UserRoles.VIEWER]
  }
];

const DeliveriesTable: React.FC<DeliveriesTableProps> = ({ 
  deliveries, 
  loading,
  onDeliveryUpdated
}) => {
  // Estado para el filtro global
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
  const [filters, setFilters] = useState({
    global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS }
  });
  const { userRole } = useUserRole();
  const { createGuide, cancelGuide, loading: guideActionLoading } = usePaquetexpress();
  const toast = useRef<Toast>(null);
  const [processingIds, setProcessingIds] = useState<string[]>([]);
  const [showCreateGuideDialog, setShowCreateGuideDialog] = useState<boolean>(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);

  // Manejar cambio en el input de búsqueda
  const onGlobalFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  }, [filters]);

  // Función para filtrar columnas según el rol del usuario
  const filterColumnsByRole = useCallback((column: ColumnConfig, role: string | null): boolean => {
    // Si no hay roles restringidos, mostrar la columna a todos los usuarios
    if (!column.restrictedRoles || column.restrictedRoles.length === 0) {
      return true;
    }
    
    // Si hay roles restringidos, verificar si el usuario NO tiene uno de esos roles
    return role ? !column.restrictedRoles.includes(role) : true;
  }, []);

  // Funciones para mostrar mensajes Toast
  const showSuccess = useCallback((message: string) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Éxito',
      detail: message,
      life: 3000
    });
  }, []);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 3000
    });
  }, []);

  // Manejadores para acciones de guía
  const handleCreateGuide = useCallback((delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setShowCreateGuideDialog(true);
  }, []);

  const handleConfirmCreateGuide = useCallback(async (totalWeight: number, quantityPackages: number) => {
    if (!selectedDelivery) return;
    
    const deliveryId = selectedDelivery.ENTREGA;
    if (processingIds.includes(deliveryId)) return;
    
    setProcessingIds(prev => [...prev, deliveryId]);
    
    try {
      const result = await createGuide(deliveryId, totalWeight, quantityPackages);
      if (result.success) {
        showSuccess('Guía creada exitosamente');
        setShowCreateGuideDialog(false);
        if (onDeliveryUpdated) onDeliveryUpdated();
      } else {
        showError(result.errorMessage || 'Error al crear la guía');
      }
    } catch (error) {
      showError('Error al crear la guía');
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== deliveryId));
      setSelectedDelivery(null);
    }
  }, [selectedDelivery, processingIds, createGuide, showSuccess, showError, onDeliveryUpdated]);

  const handleCancelCreateGuide = useCallback(() => {
    setShowCreateGuideDialog(false);
    setSelectedDelivery(null);
  }, []);

  const handleCancelGuide = useCallback(async (trackingNumber: string) => {
    // Prevenir abrir el diálogo si ya está procesando esta guía o hay alguna operación en curso
    if (processingIds.includes(trackingNumber) || guideActionLoading) return;
    
    confirmDialog({
      message: `¿Estás seguro de cancelar la guía ${trackingNumber}?`,
      header: 'Confirmar Cancelación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, cancelar',
      rejectLabel: 'No, volver',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        if (processingIds.includes(trackingNumber)) return;
        
        setProcessingIds(prev => [...prev, trackingNumber]);
        try {
          const result = await cancelGuide(trackingNumber);
          if (result.success) {
            showSuccess('Guía cancelada exitosamente');
            if (onDeliveryUpdated) onDeliveryUpdated();
          } else {
            showError(result.errorMessage || 'Error al cancelar la guía');
          }
        } catch (error) {
          showError('Error al cancelar la guía');
        } finally {
          setProcessingIds(prev => prev.filter(id => id !== trackingNumber));
        }
      }
    });
  }, [cancelGuide, processingIds, guideActionLoading, showSuccess, showError, onDeliveryUpdated]);

  const handlePrintGuide = useCallback(async (trackingNumber: string) => {
    window.open(`${PRINT_URL}${trackingNumber}&measure=4x6`, '_blank');
  }, []);

  // Función para renderizar la columna de acciones
  const actionsBodyTemplate = useCallback((rowData: Delivery) => {
    const isProcessingCancel = rowData.NUMERO_RASTREO ? processingIds.includes(rowData.NUMERO_RASTREO) : false;
    const isLoading = loading || guideActionLoading;
    
    return (
      <div className="actions-column">
        <Button
          icon="pi pi-plus"
          className="p-button-rounded p-button-text p-button-success mr-2"
          tooltip="Crear"
          tooltipOptions={{ position: 'left' }}
          onClick={() => handleCreateGuide(rowData)}
          disabled={isLoading || !rowData.ENTREGA || !!rowData.NUMERO_RASTREO}
        />
        
        {isProcessingCancel ? (
          <ProgressSpinner style={{ width: '25px', height: '25px' }} />
        ) : (
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-text p-button-danger"
            tooltip="Cancelar"
            tooltipOptions={{ position: 'left' }}
            onClick={() => rowData.NUMERO_RASTREO ? handleCancelGuide(rowData.NUMERO_RASTREO) : undefined}
            disabled={isLoading || !rowData.NUMERO_RASTREO || isProcessingCancel}
          />
        )}

        <Button
          icon="pi pi-print"
          className="p-button-rounded p-button-text p-button-info mr-2"
          tooltip="Imprimir"
          tooltipOptions={{ position: 'left' }}
          onClick={() => rowData.NUMERO_RASTREO ? handlePrintGuide(rowData.NUMERO_RASTREO) : undefined}
          disabled={isLoading || !rowData.NUMERO_RASTREO}
          />
      </div>
    );
  }, [processingIds, loading, guideActionLoading, handleCreateGuide, handleCancelGuide, handlePrintGuide]);

  // Filtrar columnas según el rol del usuario
  const visibleColumns = useMemo(() => {
    return COLUMN_CONFIGS.filter(column => filterColumnsByRole(column, userRole))
      .map(column => {
        // Asignar el template de acciones a la columna de acciones
        if (column.field === 'actions') {
          return { ...column, body: actionsBodyTemplate };
        }
        return column;
      });
  }, [userRole, filterColumnsByRole, actionsBodyTemplate]);

  // Componente para el header con filtro global
  const header = useMemo(() => {
    return <TableHeader 
      globalFilterValue={globalFilterValue} 
      onGlobalFilterChange={onGlobalFilterChange} 
    />;
  }, [globalFilterValue, onGlobalFilterChange]);

  // Obtener los campos de filtro global según las columnas visibles
  const getGlobalFilterFields = useCallback((columns: ColumnConfig[]) => {
    return columns.map(column => column.field);
  }, []);

  // Obtener los campos de filtro global según las columnas visibles
  const globalFilterFields = useMemo(() => {
    return getGlobalFilterFields(visibleColumns);
  }, [visibleColumns, getGlobalFilterFields]);

  return (
    <div className="deliveries-table-container">
      <Toast ref={toast} />
      <ConfirmDialog />
      <DataTable
        value={deliveries}
        paginator
        rows={20}
        rowsPerPageOptions={[20, 50, 100, 200]}
        tableStyle={{ minWidth: '50rem' }}
        loading={loading}
        filters={filters}
        globalFilterFields={globalFilterFields} 
        header={header}
        emptyMessage="No se encontraron entregas en el rango de fechas seleccionado"
        size="small"
        stripedRows
        pt={{
          table: { style: { fontSize: '0.875rem' } }
        }}
      >
        {visibleColumns.map((column) => (
          <Column
            key={column.field.toString()}
            field={column.field.toString()}
            header={column.header}
            sortable={column.sortable}
            style={column.style}
            body={column.body}
          />
        ))}
      </DataTable>
      
      <CreateGuideDialog
        visible={showCreateGuideDialog}
        onHide={handleCancelCreateGuide}
        onConfirm={handleConfirmCreateGuide}
        deliveryId={selectedDelivery?.ENTREGA || ''}
        defaultWeight={selectedDelivery ? Number(selectedDelivery.PESO_TOTAL) : 0}
        defaultQuantity={selectedDelivery ? Number(selectedDelivery.CANTIDAD_BULTOS) : 0}
        loading={guideActionLoading}
      />
    </div>
  );
};

export default DeliveriesTable; 